import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, ArrowRight, Hash, Lock, Mail } from 'lucide-react';
import { PASSWORD_REQUIREMENTS_HINT } from '@joshua/domain/password';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  RECOVERY_IDLE_TIMEOUT_MS,
  normalizeRecoveryEmail,
} from '@/services/recuperarSenha.service';
import type { ProcessedBranding } from '@/types/tenant';

/** Auth allows one recovery email per user every 60 s (smtp_max_frequency). */
export const RESEND_COOLDOWN_MS = 60 * 1000;

const CODE_LENGTH = 6;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Outside component state on purpose: closing recovery resets every field,
// but must never re-enable an early resend.
let lastRequestAt = 0;

/** Test hook: forget the last request time. */
export function resetRecoveryCooldown(): void {
  lastRequestAt = 0;
}

function secondsLeft(now: number): number {
  return Math.max(0, Math.ceil((lastRequestAt + RESEND_COOLDOWN_MS - now) / 1000));
}

function formatCountdown(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

type Step = 'request' | 'code';

interface RecuperarSenhaFormProps {
  branding: ProcessedBranding;
  /** Email typed on the login form; prefilled when valid. */
  initialEmail: string;
  /** Back to the login form. */
  onClose: () => void;
}

/**
 * Password recovery inside the login card (JOSH-73, mockup option 1).
 *
 * request → code → new password. The new-password step follows
 * `recoveryPending` from the auth context, so it is the only step shown while
 * a verified recovery waits for its password.
 */
export default function RecuperarSenhaForm({ branding, initialEmail, onClose }: RecuperarSenhaFormProps) {
  const {
    requestRecoveryCode,
    verifyRecoveryCode,
    completeRecovery,
    abandonRecovery,
    recoveryPending,
    recoveryNotice,
    dismissRecoveryNotice,
  } = useAuth();
  const { showWarning } = useToast();

  const prefill = recoveryNotice?.email || initialEmail;
  const prefillIsValid = EMAIL_PATTERN.test(normalizeRecoveryEmail(prefill));

  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState(prefillIsValid ? normalizeRecoveryEmail(prefill) : '');
  // "Já tenho um código": the code step also asks for the email.
  const [askEmailOnCode, setAskEmailOnCode] = useState(false);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(recoveryNotice?.message ?? '');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(() => secondsLeft(Date.now()));
  const [lastTyped, setLastTyped] = useState(() => Date.now());

  const abandonRef = useRef(abandonRecovery);
  abandonRef.current = abandonRecovery;

  // An abandonment (here, another tab or a cold start) hands back the email
  // and message: restart on the request step with them.
  useEffect(() => {
    if (!recoveryNotice) return;
    setStep('request');
    setEmail(normalizeRecoveryEmail(recoveryNotice.email));
    setNotice(recoveryNotice.message);
    setCode('');
    setPassword('');
    setConfirm('');
    setError('');
    dismissRecoveryNotice();
  }, [recoveryNotice, dismissRecoveryNotice]);

  // Countdown driven by the last request time. A per-effect flag, never a
  // mounted ref, so StrictMode's double mount cannot leave a stale interval.
  const counting = cooldown > 0;
  useEffect(() => {
    if (!counting) return;
    let cancelled = false;
    const id = window.setInterval(() => {
      if (!cancelled) setCooldown(secondsLeft(Date.now()));
    }, 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [counting]);

  // A verified code left idle for 10 minutes is abandoned.
  useEffect(() => {
    if (!recoveryPending) return;
    let cancelled = false;
    const id = window.setTimeout(() => {
      if (!cancelled) void abandonRef.current();
    }, RECOVERY_IDLE_TIMEOUT_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [recoveryPending, lastTyped]);

  const leave = async () => {
    if (recoveryPending) {
      await abandonRecovery(); // the notice effect reopens the request step
      return;
    }
    onClose();
  };

  const checkEmail = (): string | null => {
    const normalized = normalizeRecoveryEmail(email);
    if (!EMAIL_PATTERN.test(normalized)) {
      setError('Digite um e-mail válido.');
      return null;
    }
    return normalized;
  };

  const sendCode = async () => {
    const target = checkEmail();
    if (!target) return;
    setBusy(true);
    setError('');
    const result = await requestRecoveryCode(target);
    setBusy(false);
    if (result.outcome !== 'sent') {
      setError(result.message);
      return;
    }
    lastRequestAt = Date.now();
    setCooldown(secondsLeft(lastRequestAt));
    setEmail(target);
    setNotice('');
    setAskEmailOnCode(false);
    setCode('');
    setStep('code');
  };

  const verify = async () => {
    const target = checkEmail();
    if (!target) return;
    const digits = code.replace(/\D/g, '');
    if (digits.length !== CODE_LENGTH) {
      setError(`Digite os ${CODE_LENGTH} dígitos do código.`);
      return;
    }
    setBusy(true);
    setError('');
    const result = await verifyRecoveryCode(target, digits);
    setBusy(false);
    if (!result.ok) setError(result.message);
  };

  const save = async () => {
    if (password !== confirm) {
      setError('As senhas não conferem.');
      return;
    }
    setBusy(true);
    setError('');
    sessionStorage.setItem('joshua_fresh_login', 'true');
    const result = await completeRecovery(password);
    setBusy(false);
    if (result.ok) {
      if (result.notice) showWarning(result.notice, { duration: 10000 });
      return; // the app takes over from here
    }
    sessionStorage.removeItem('joshua_fresh_login');
    setError(result.message);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (recoveryPending) void save();
    else if (step === 'request') void sendCode();
    else void verify();
  };

  const submitLabel = recoveryPending ? 'Salvar e entrar' : step === 'request' ? 'Enviar código' : 'Continuar';
  const busyLabel = recoveryPending ? 'Salvando...' : step === 'request' ? 'Enviando...' : 'Confirmando...';

  const emailField = (autoFocus: boolean) => (
    <label className="auth-field">
      <span className="auth-field-label">E-mail</span>
      <div className="auth-field-control">
        <span className="auth-field-icon" aria-hidden="true">
          <Mail size={16} strokeWidth={1.75} />
        </span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="voce@empresa.com.br"
          autoComplete="email"
          className="auth-field-input"
          disabled={busy}
          autoFocus={autoFocus}
        />
      </div>
    </label>
  );

  const passwordField = (label: string, value: string, onChange: (v: string) => void, autoFocus = false) => (
    <label className="auth-field">
      <span className="auth-field-label">{label}</span>
      <div className="auth-field-control">
        <span className="auth-field-icon" aria-hidden="true">
          <Lock size={16} strokeWidth={1.75} />
        </span>
        <input
          type="password"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setLastTyped(Date.now());
          }}
          autoComplete="new-password"
          className="auth-field-input"
          disabled={busy}
          autoFocus={autoFocus}
        />
      </div>
    </label>
  );

  let greeting = 'Vamos recuperar seu acesso.';
  if (recoveryPending) greeting = 'Escolha a sua nova senha.';

  let sub: string;
  if (recoveryPending) sub = 'Depois de salvar, você entra direto no sistema.';
  else if (step === 'request') sub = notice || 'Enviaremos um código de 6 dígitos para o seu e-mail.';
  else if (askEmailOnCode) sub = 'Use o código de 6 dígitos que chegou no seu e-mail. Ele vale por 30 minutos.';
  else sub = `Se existir uma conta para ${email}, enviamos um código. Ele vale por 30 minutos.`;

  return (
    <>
      {branding.logoUrl && (
        <div className="auth-card-logo-wrapper">
          <img src={branding.logoUrl} alt={`${branding.tenantName} Logo`} className="auth-card-logo" />
        </div>
      )}

      {!recoveryPending && (
        <button type="button" className="auth-back" onClick={() => void leave()} disabled={busy}>
          ← Voltar ao login
        </button>
      )}

      <p className="auth-greeting">{greeting}</p>
      <p className={`auth-sub${step === 'request' && notice && !recoveryPending ? ' auth-sub--alert' : ''}`}>
        {sub}
      </p>

      <form onSubmit={onSubmit} className="auth-form" noValidate>
        {recoveryPending ? (
          <>
            {/* Lets password managers pair the new password with this account. */}
            <input
              type="email"
              name="username"
              autoComplete="username"
              value={email}
              readOnly
              hidden
            />
            {passwordField('Nova senha', password, setPassword, true)}
            <p className="auth-hint">{PASSWORD_REQUIREMENTS_HINT}</p>
            {passwordField('Confirmar nova senha', confirm, setConfirm)}
          </>
        ) : step === 'request' ? (
          emailField(!prefillIsValid)
        ) : (
          <>
            {askEmailOnCode && emailField(!EMAIL_PATTERN.test(normalizeRecoveryEmail(email)))}
            <label className="auth-field">
              <span className="auth-field-label">Código</span>
              <div className="auth-field-control">
                <span className="auth-field-icon" aria-hidden="true">
                  <Hash size={16} strokeWidth={1.75} />
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
                  placeholder="000000"
                  aria-invalid={error ? 'true' : 'false'}
                  className="auth-field-input auth-field-input--code"
                  disabled={busy}
                  autoFocus={!askEmailOnCode || EMAIL_PATTERN.test(normalizeRecoveryEmail(email))}
                />
              </div>
            </label>
          </>
        )}

        <div aria-live="polite">
          {error && (
            <div className="auth-error" role="alert">
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={busy}
          className="auth-button"
          style={{ background: branding.primaryColor, borderColor: branding.primaryColor }}
        >
          {busy ? (
            <>
              <span className="auth-button-spinner" aria-hidden="true" />
              <span>{busyLabel}</span>
            </>
          ) : (
            <>
              <span>{submitLabel}</span>
              <ArrowRight size={16} strokeWidth={2} />
            </>
          )}
        </button>

        {!recoveryPending && step === 'request' && (
          <button
            type="button"
            className="auth-link"
            onClick={() => {
              setError('');
              setNotice('');
              setAskEmailOnCode(true);
              setStep('code');
            }}
          >
            Já tenho um código
          </button>
        )}

        {!recoveryPending && step === 'code' && (
          <button
            type="button"
            className="auth-link"
            onClick={() => void sendCode()}
            disabled={busy || cooldown > 0}
            aria-live="polite"
          >
            {cooldown > 0 ? `Reenviar código em ${formatCountdown(cooldown)}` : 'Reenviar código'}
          </button>
        )}
      </form>
    </>
  );
}
